/**
 * Minimal, allocation-light binary codec used for the entire wire protocol.
 * Little-endian everywhere. The writer grows its backing buffer as needed and
 * hands back a zero-copy subarray view; the reader walks a DataView in place.
 *
 * Browsers and Node both expose DataView/Uint8Array, so this file is shared
 * verbatim by the server, the client and the load bots.
 */

export class BinaryWriter {
  private view: DataView;
  private bytes: Uint8Array;
  private offset = 0;

  constructor(initialCapacity = 256) {
    const buf = new ArrayBuffer(initialCapacity);
    this.bytes = new Uint8Array(buf);
    this.view = new DataView(buf);
  }

  private ensure(extra: number): void {
    const needed = this.offset + extra;
    if (needed <= this.bytes.byteLength) return;
    let cap = this.bytes.byteLength * 2;
    while (cap < needed) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.bytes);
    this.bytes = next;
    this.view = new DataView(next.buffer);
  }

  u8(v: number): this { this.ensure(1); this.view.setUint8(this.offset, v & 0xff); this.offset += 1; return this; }
  i8(v: number): this { this.ensure(1); this.view.setInt8(this.offset, v); this.offset += 1; return this; }
  u16(v: number): this { this.ensure(2); this.view.setUint16(this.offset, v & 0xffff, true); this.offset += 2; return this; }
  i16(v: number): this { this.ensure(2); this.view.setInt16(this.offset, v, true); this.offset += 2; return this; }
  u32(v: number): this { this.ensure(4); this.view.setUint32(this.offset, v >>> 0, true); this.offset += 4; return this; }
  i32(v: number): this { this.ensure(4); this.view.setInt32(this.offset, v, true); this.offset += 4; return this; }
  f32(v: number): this { this.ensure(4); this.view.setFloat32(this.offset, v, true); this.offset += 4; return this; }
  f64(v: number): this { this.ensure(8); this.view.setFloat64(this.offset, v, true); this.offset += 8; return this; }

  /** Unsigned LEB128 varint — compact for small ids/counts. */
  varint(v: number): this {
    let n = v >>> 0;
    do {
      let b = n & 0x7f;
      n >>>= 7;
      if (n !== 0) b |= 0x80;
      this.u8(b);
    } while (n !== 0);
    return this;
  }

  /** Length-prefixed UTF-8 string (u16 length). */
  str(s: string): this {
    const enc = textEncoder.encode(s);
    this.u16(enc.length);
    this.ensure(enc.length);
    this.bytes.set(enc, this.offset);
    this.offset += enc.length;
    return this;
  }

  raw(b: Uint8Array): this {
    this.ensure(b.length);
    this.bytes.set(b, this.offset);
    this.offset += b.length;
    return this;
  }

  /** Zero-copy view of the bytes written so far. */
  finish(): Uint8Array {
    return this.bytes.subarray(0, this.offset);
  }

  get length(): number { return this.offset; }
}

/**
 * Thrown when a read would run past the end of the buffer (a truncated or
 * malformed packet). It is a distinct, catchable type so the message-dispatch
 * layer can reject the packet / drop it instead of letting a raw DataView
 * `RangeError` escape and take the whole process down. See the message loops
 * in apps/server (roomManager.onConnection, room.handleMessage).
 */
export class DecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecodeError';
  }
}

export class BinaryReader {
  private view: DataView;
  private offset: number;
  private end: number;

  constructor(data: ArrayBuffer | Uint8Array, byteOffset = 0, byteLength?: number) {
    // Defensive clamping: an attacker-supplied packet may be shorter than the
    // caller's assumed header (e.g. a 1-byte "hello" read from offset 1), which
    // would otherwise make the DataView constructor itself throw a RangeError.
    if (data instanceof Uint8Array) {
      const off = Math.max(0, Math.min(byteOffset, data.byteLength));
      const len = byteLength ?? Math.max(0, data.byteLength - off);
      this.view = new DataView(data.buffer, data.byteOffset + off, len);
    } else {
      const off = Math.max(0, Math.min(byteOffset, data.byteLength));
      const len = byteLength ?? Math.max(0, data.byteLength - off);
      this.view = new DataView(data, off, len);
    }
    this.offset = 0;
    this.end = this.view.byteLength;
  }

  get remaining(): number { return this.end - this.offset; }
  get done(): boolean { return this.offset >= this.end; }

  /** Guard: ensure `n` more bytes are readable, else throw a catchable DecodeError. */
  private need(n: number): void {
    if (this.offset + n > this.end) {
      throw new DecodeError(`read past end: need ${n} at ${this.offset}/${this.end}`);
    }
  }

  u8(): number { this.need(1); const v = this.view.getUint8(this.offset); this.offset += 1; return v; }
  i8(): number { this.need(1); const v = this.view.getInt8(this.offset); this.offset += 1; return v; }
  u16(): number { this.need(2); const v = this.view.getUint16(this.offset, true); this.offset += 2; return v; }
  i16(): number { this.need(2); const v = this.view.getInt16(this.offset, true); this.offset += 2; return v; }
  u32(): number { this.need(4); const v = this.view.getUint32(this.offset, true); this.offset += 4; return v; }
  i32(): number { this.need(4); const v = this.view.getInt32(this.offset, true); this.offset += 4; return v; }
  f32(): number { this.need(4); const v = this.view.getFloat32(this.offset, true); this.offset += 4; return v; }
  f64(): number { this.need(8); const v = this.view.getFloat64(this.offset, true); this.offset += 8; return v; }

  varint(): number {
    let result = 0;
    let shift = 0;
    // A u32 LEB128 is at most 5 bytes; cap iterations so a stream of high-bit
    // bytes can't spin or shift past 32 bits. u8() bounds-checks each byte.
    for (let i = 0; i < 5; i++) {
      const b = this.u8();
      result |= (b & 0x7f) << shift;
      if ((b & 0x80) === 0) return result >>> 0;
      shift += 7;
    }
    throw new DecodeError('varint too long');
  }

  str(): string {
    const len = this.u16();
    this.need(len); // reject a length that claims more bytes than remain
    const start = this.view.byteOffset + this.offset;
    const slice = new Uint8Array(this.view.buffer, start, len);
    this.offset += len;
    return textDecoder.decode(slice);
  }
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
